import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Validate that a URL is a valid HTTP/HTTPS URL
 */
function isValidUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Create Supabase client safely, handling invalid configurations
 */
function createSupabaseClient(): SupabaseClient | null {
  // Check if credentials are provided
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase credentials not configured. Online features will be unavailable.')
    return null
  }
  
  // Validate URL format before attempting to create client
  if (!isValidUrl(supabaseUrl)) {
    console.warn('Invalid Supabase URL configured. Online features will be unavailable.')
    return null
  }
  
  // Check for placeholder values
  if (supabaseUrl.includes('your-project') || supabaseAnonKey.includes('your-')) {
    console.warn('Supabase credentials contain placeholder values. Online features will be unavailable.')
    return null
  }
  
  try {
    return createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // Don't auto-refresh tokens on init - we'll do it manually when needed
        autoRefreshToken: true,
        // Persist session to localStorage
        persistSession: true,
        // Detect session from URL (for OAuth redirects)
        detectSessionInUrl: true,
      },
      // Disable realtime by default - it's not needed for initial load
      // and can cause connection delays
      realtime: {
        params: {
          eventsPerSecond: 2,
        },
      },
      global: {
        // Add fetch options with shorter timeout
        fetch: (url, options) => {
          return fetch(url, {
            ...options,
            // Add a signal for timeout if not already present
          });
        },
      },
    })
  } catch (error) {
    console.warn('Failed to create Supabase client:', error)
    return null
  }
}

export const supabase = createSupabaseClient()

export const isSupabaseConfigured = () => !!supabase

/**
 * Get Supabase URL for raw API calls
 */
export const getSupabaseUrl = () => supabaseUrl

/**
 * Get Supabase anon key for raw API calls
 */
export const getSupabaseAnonKey = () => supabaseAnonKey

/**
 * Make a raw fetch request to Supabase REST API
 * This bypasses the Supabase client which can have auth state issues
 */
export async function supabaseRawFetch<T>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: unknown;
    accessToken?: string;
    timeoutMs?: number;
  } = {}
): Promise<{ data: T | null; error: string | null }> {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { data: null, error: 'Supabase not configured' };
  }

  const { method = 'GET', body, accessToken, timeoutMs = 10000 } = options;
  
  // Set up AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const headers: Record<string, string> = {
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${accessToken || supabaseAnonKey}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation',
    };

    const response = await fetch(`${supabaseUrl}/rest/v1/${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[Supabase] Raw fetch error: ${response.status} - ${errorText}`);
      return { data: null, error: `HTTP ${response.status}: ${errorText}` };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof Error && e.name === 'AbortError') {
      console.error(`[Supabase] Request timeout after ${timeoutMs}ms`);
      return { data: null, error: `Request timeout after ${timeoutMs}ms` };
    }
    const error = e instanceof Error ? e.message : 'Unknown error';
    console.error(`[Supabase] Raw fetch exception:`, e);
    return { data: null, error };
  }
}

// ============== Simple Cache Layer ==============

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const DEFAULT_CACHE_TTL = 30000; // 30 seconds

/**
 * Get data from cache if still valid
 */
export function getCached<T>(key: string, ttlMs: number = DEFAULT_CACHE_TTL): T | null {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;
  
  if (Date.now() - entry.timestamp > ttlMs) {
    cache.delete(key);
    return null;
  }
  
  return entry.data;
}

/**
 * Store data in cache
 */
export function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Clear specific cache entry or all cache
 */
export function clearCache(key?: string): void {
  if (key) {
    cache.delete(key);
  } else {
    cache.clear();
  }
}

/**
 * Cached fetch wrapper - returns cached data if available, otherwise fetches
 */
export async function cachedFetch<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_CACHE_TTL
): Promise<T> {
  const cached = getCached<T>(cacheKey, ttlMs);
  if (cached !== null) {
    console.debug(`[Cache] Hit: ${cacheKey}`);
    return cached;
  }
  
  console.debug(`[Cache] Miss: ${cacheKey}`);
  const data = await fetcher();
  setCache(cacheKey, data);
  return data;
}

/**
 * Diagnostic function to test raw API connectivity
 * This bypasses the Supabase client to test if the issue is with the client or network
 */
export async function testSupabaseConnectivity(): Promise<{ success: boolean; latencyMs: number; error?: string }> {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { success: false, latencyMs: 0, error: 'Not configured' };
  }

  const startTime = Date.now();
  try {
    const response = await fetch(`${supabaseUrl}/rest/v1/rooms?select=id&limit=1`, {
      method: 'GET',
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
    });
    const latencyMs = Date.now() - startTime;
    
    if (response.ok) {
      console.debug(`[Supabase] Raw API connectivity test: OK (${latencyMs}ms)`);
      return { success: true, latencyMs };
    } else {
      const error = `HTTP ${response.status}`;
      console.warn(`[Supabase] Raw API connectivity test: FAILED - ${error} (${latencyMs}ms)`);
      return { success: false, latencyMs, error };
    }
  } catch (e) {
    const latencyMs = Date.now() - startTime;
    const error = e instanceof Error ? e.message : 'Unknown error';
    console.warn(`[Supabase] Raw API connectivity test: ERROR - ${error} (${latencyMs}ms)`);
    return { success: false, latencyMs, error };
  }
}

// Run connectivity test on load (for debugging)
if (supabase) {
  testSupabaseConnectivity();
}

/**
 * Clear all Supabase auth state from localStorage
 * Call this if you're experiencing auth issues
 */
export function clearSupabaseAuthState(): void {
  console.debug('[Supabase] Clearing auth state from localStorage...');
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => {
    console.debug(`[Supabase] Removing: ${key}`);
    localStorage.removeItem(key);
  });
  console.debug(`[Supabase] Cleared ${keysToRemove.length} keys`);
}

/**
 * Try to get access token from localStorage as fallback
 */
function getAccessTokenFromLocalStorage(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        const value = localStorage.getItem(key);
        if (value) {
          const parsed = JSON.parse(value);
          if (parsed?.access_token) {
            return parsed.access_token;
          }
        }
      }
    }
  } catch (e) {
    console.warn('[Supabase] Failed to get token from localStorage:', e);
  }
  return null;
}

/**
 * Get the current access token from Supabase session
 * Returns null if not authenticated or if timeout
 */
export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  
  try {
    // Add timeout to prevent hanging (increased to 5s)
    const timeoutPromise = new Promise<'timeout'>((resolve) => {
      setTimeout(() => resolve('timeout'), 5000);
    });
    
    const sessionPromise = supabase.auth.getSession().then(
      ({ data: { session } }) => session?.access_token || null
    );
    
    const result = await Promise.race([sessionPromise, timeoutPromise]);
    
    // If we got a valid token, return it
    if (result && result !== 'timeout') {
      return result;
    }
    
    // If timeout or null, try localStorage fallback
    if (result === 'timeout') {
      console.warn('[Supabase] getSession timed out, trying localStorage fallback');
    }
    
    const localToken = getAccessTokenFromLocalStorage();
    if (localToken) {
      console.debug('[Supabase] Using access token from localStorage');
      return localToken;
    }
    
    return null;
  } catch (e) {
    console.warn('[Supabase] Failed to get access token:', e);
    // Try localStorage as last resort
    return getAccessTokenFromLocalStorage();
  }
}

// ============== Shared Service Utilities ==============

/** Result type for service operations */
export type ServiceResult<T> = { data: T; error: null } | { data: null; error: string };

/**
 * Check if Supabase is available, return error if not
 */
export function requireSupabase(): ServiceResult<SupabaseClient> {
  if (!isSupabaseConfigured() || !supabase) {
    return { data: null, error: 'Supabase not configured' };
  }
  return { data: supabase, error: null };
}

/**
 * Get current user ID, return error if not authenticated
 */
export async function requireAuth(): Promise<ServiceResult<string>> {
  const check = requireSupabase();
  if (check.error || !check.data) return { data: null, error: check.error || 'Supabase not available' };
  
  try {
    const { data: { user } } = await check.data.auth.getUser();
    if (!user?.id) {
      return { data: null, error: 'Not authenticated' };
    }
    return { data: user.id, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Auth check failed' };
  }
}

/**
 * Get display username with fallback
 */
export function getDisplayUsername(
  profile: { username?: string | null; display_name?: string | null } | null,
  userId: string
): string {
  return profile?.display_name || profile?.username || `user_${userId.substring(0, 8)}`;
}

/**
 * Build a Supabase query string with filters
 */
export function buildQueryString(
  table: string,
  options: {
    select?: string;
    filters?: Record<string, string | number | boolean>;
    order?: { column: string; ascending?: boolean };
    limit?: number;
    offset?: number;
  } = {}
): string {
  const { select = '*', filters = {}, order, limit, offset } = options;
  
  const parts = [`${table}?select=${encodeURIComponent(select)}`];
  
  for (const [key, value] of Object.entries(filters)) {
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  }
  
  if (order) {
    parts.push(`order=${order.column}.${order.ascending ? 'asc' : 'desc'}`);
  }
  
  if (limit !== undefined) {
    parts.push(`limit=${limit}`);
  }
  
  if (offset !== undefined) {
    parts.push(`offset=${offset}`);
  }
  
  return parts.join('&');
}

/**
 * Calculate time ago date for filtering
 */
export function getTimeAgoDate(period: 'week' | 'month' | 'all'): Date | null {
  if (period === 'all') return null;
  
  const now = new Date();
  if (period === 'week') {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  }
  if (period === 'month') {
    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }
  return null;
}
